import { isToday, startOfTomorrow } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useCookie } from 'react-use';

import {
  useContactSalesMutation,
  useGetLoggedInUser,
  useUserTrialDetails,
} from '@revelio/auth';
import { CTAInteractionTrackingEvents, useTourStore } from '@revelio/core';
import { type KeyValueResp, MetadataKey } from '@revelio/data-access';
import { useDisclosure } from '@revelio/ui';

import { useUserMetadata } from '../../../../hooks/admin/useUserMetadata';
import { useBrand } from '../../../../shared/branding/brand-provider';
import { useToast } from '../../../../shared/hooks/useToast';
import {
  DEFAULT_TUTORIAL_VIDEO_ID,
  findTutorialVideoForPath,
  Tutorial,
} from '../../../../shared/ui/tutorial/Tutorial';
import { useIsTourDone } from '../../../tour/use-is-tour-done';
import {
  toastContactSalesAlreadyNotified,
  toastContactSalesRequestError,
  toastContactSalesRequestSuccess,
} from '../../../trialUser/contact-sales-toast';
import { useFreeTrialAlertModalState } from '../../../trialUser/useFreeTrialAlertModalState';
import { isTutorialSectionAllowed } from '../../../tutorial/tutorial-section-access';

export interface RailTutorials {
  hasUnwatchedVideo: boolean;
  openTutorials: () => void;
}

/** Opens the Tutorials modal and anchors the once-a-day trial tutorial popup. */
export const TutorialsAnchor = ({
  children,
}: {
  children: (tutorials: RailTutorials | undefined) => ReactNode;
}) => {
  const location = useLocation();
  const [autoOpen, setAutoOpen] = useState(false);

  const toast = useToast();
  const { loggedInUser, refetch: refetchUserData } = useGetLoggedInUser();
  const { updateMetadata } = useUserMetadata();
  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });
  const { email } = loggedInUser;
  const { days } = useFreeTrialAlertModalState();

  const { metadata } = loggedInUser;

  const [openedTutorials, updateOpenedTutorials] =
    useCookie('opened-tutorials');

  const isWhitelabel = useBrand().clientName !== 'default';
  const showTutorial = !isWhitelabel;

  const userWatchedIds = useMemo(() => {
    return new Set(
      JSON.parse(
        metadata?.find(
          (item: KeyValueResp | null) =>
            item?.key === MetadataKey.StepsCompleted
        )?.value || '[]'
      )
    );
  }, [metadata]);

  const mixpanelContactSales = () => {
    mixpanel.track(CTAInteractionTrackingEvents.CONTACT_SALES_CLICK, {
      days_left: days,
    });
  };

  const { mutate: contactSales } = useContactSalesMutation();

  const handleContactSales = () => {
    mixpanelContactSales();

    const contactedSales = localStorage.getItem('contacted-sales') || '';
    if (!contactedSales || !isToday(new Date(contactedSales))) {
      contactSales(
        { email, trialUpgrade: location.pathname },
        {
          onSuccess: () => {
            localStorage.setItem('contacted-sales', new Date().toDateString());
            toastContactSalesRequestSuccess(toast);
          },
          onError: () => {
            toastContactSalesRequestError(toast);
          },
        }
      );
    } else {
      toastContactSalesAlreadyNotified(toast);
    }
  };

  const handleTutorialSeen = async () => {
    try {
      if (!videoData?.id) return;
      if (userWatchedIds.has(videoData.id)) return;

      const updatedIds = JSON.stringify([
        ...Array.from(userWatchedIds),
        videoData.id,
      ]);

      await updateMetadata(loggedInUser, [
        {
          key: MetadataKey.StepsCompleted,
          value: updatedIds,
        },
      ]);

      // Ensure UI reflects latest metadata
      if (typeof refetchUserData === 'function') {
        refetchUserData();
      }
    } catch {
      // noop
    }
  };

  const videoData = useMemo(() => {
    if (!showTutorial) return null;
    return findTutorialVideoForPath(location.pathname);
  }, [location.pathname, showTutorial]);

  const videoId = useMemo(() => videoData?.id, [videoData]);

  const userTabs = useMemo(() => loggedInUser.tabs ?? [], [loggedInUser.tabs]);
  const canOpenTutorialSection = isTutorialSectionAllowed(
    videoData?.paths[0],
    userTabs
  );

  // A new user's guided tour opens on the same page load. The popup waits for
  // it to be finished or skipped: underneath the tour's overlay it went unseen
  // and timed out, spending the day's offer.
  const isTour = useTourStore((s) => s.isTour);
  const isTourDone = useIsTourDone();

  useEffect(() => {
    if (
      showTutorial &&
      videoId &&
      !userWatchedIds.has(videoId) &&
      isTrialUser &&
      isTourDone &&
      !isTour
    ) {
      setAutoOpen(true);
    } else {
      setAutoOpen(false);
    }
  }, [showTutorial, videoId, userWatchedIds, isTrialUser, isTourDone, isTour]);

  const {
    isOpen: isTutorialOpen,
    onOpen: onTutorialOpen,
    onClose: onTutorialClose,
  } = useDisclosure();

  // The once-a-day auto-open gets exactly one shot per video per mount.
  // `autoOpen` only flips once the logged-in user's metadata resolves, so a
  // menu click can open the modal before that response lands; the late
  // auto-open must not then fire on top of it.
  const autoOpenSettledVideoIds = useRef<Set<string>>(new Set());
  const settleAutoOpen = useCallback(() => {
    if (videoId) autoOpenSettledVideoIds.current.add(videoId);
  }, [videoId]);

  useEffect(() => {
    if (!showTutorial || !autoOpen || !videoId) return;
    if (autoOpenSettledVideoIds.current.has(videoId)) return;

    const openedTutorialsArray = JSON.parse(openedTutorials || '[]');
    if (openedTutorialsArray.includes(videoId)) return;

    autoOpenSettledVideoIds.current.add(videoId);
    onTutorialOpen();
    const nextDayStart = startOfTomorrow();
    updateOpenedTutorials(JSON.stringify([...openedTutorialsArray, videoId]), {
      expires: nextDayStart,
    });
  }, [
    showTutorial,
    autoOpen,
    videoId,
    onTutorialOpen,
    updateOpenedTutorials,
    openedTutorials,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (
      showTutorial &&
      isTutorialOpen &&
      !['/deliverables/data-enrichment'].includes(location.pathname) &&
      autoOpen
    ) {
      const timer = setTimeout(() => {
        onTutorialClose();
      }, 6000);

      return () => clearTimeout(timer);
    }
    return () => null;
  }, [showTutorial, isTutorialOpen, onTutorialClose, location.pathname]);

  const setVideoId = useTourStore((s) => s.setVideoId);
  const setIsVideoOpen = useTourStore((s) => s.setIsVideoOpen);

  const openTutorials = () => {
    // The user has now settled this video by hand — the auto-open must not
    // fire on top of the modal once the metadata it waits on arrives.
    settleAutoOpen();
    onTutorialClose();
    // The modal keeps its last selection, so a page without its own video
    // resets it to the overview rather than showing whatever came before.
    setVideoId(videoData?.id ?? DEFAULT_TUTORIAL_VIDEO_ID ?? '');
    setIsVideoOpen(true, 'menu');
  };

  const handleWatchClick = () => {
    onTutorialClose();
    if (videoData?.id) {
      setVideoId(videoData.id);
    }
    setIsVideoOpen(true, 'prompt');
  };

  if (!showTutorial) return <>{children(undefined)}</>;

  const hasUnwatchedVideo = Boolean(
    videoData && !userWatchedIds.has(videoData.id)
  );

  return (
    <Tutorial
      isOpen={isTutorialOpen}
      onClose={() => {
        settleAutoOpen();
        onTutorialClose();
        setAutoOpen(false);
      }}
      videoData={videoData}
      onWatch={handleWatchClick}
      onContactSales={handleContactSales}
      onSeen={handleTutorialSeen}
      showContactSales={!canOpenTutorialSection}
      placement="right-end"
      sideOffset={4}
    >
      <div>{children({ hasUnwatchedVideo, openTutorials })}</div>
    </Tutorial>
  );
};
